/**
 * Ownly project estimator — adjustable pricing parameters (GBP).
 *
 * Edit the objects below to tune ranges. Logic lives in `estimate-project.ts`.
 *
 * The 2026-09-16 correction separates a small business site using an existing booking tool from
 * a Shopify storefront or bespoke workflow. The former starts at £2,500, while the shop
 * retains its £5,000 floor. Wider systems and apps still require a scoping call.
 * The August package catalogue and recommendation flow remain frozen.
 */

import type {
  EstimatorBookingPayments,
  EstimatorComplexity,
  EstimatorEcommerce,
  EstimatorIntegrations,
  EstimatorWebsiteTier,
} from "@/lib/project-estimator-types";

/**
 * Absolute floor for any non-empty estimate.
 *
 * The cheapest website rung, `presence`, is £1,500 (2026-09-19), so no non-empty estimate falls
 * below it. Every other rung carries its own higher base, so this only ever binds on presence.
 */
export const GLOBAL_MIN_FLOOR_GBP = 1500;

/**
 * When multiple product surfaces are in scope, we add coordination / shared-architecture work.
 */
export const MULTI_SURFACE_OVERHEAD: Record<2 | 3, { min: number; max: number }> = {
  2: { min: 2000, max: 4200 },
  3: { min: 4800, max: 9800 },
};

/**
 * The website ladder, priced by what the site DOES.
 *
 * `presence` gets a small business found and contacted (£1,500, restored 2026-09-19).
 * `sells` is a site that brings in enquiries. `transacts` covers a straightforward booking or
 * payment journey using an established provider. Shops, accounts, and bespoke workflows add
 * their own costs below. Page count alone does not select a tier.
 */
export const WEBSITE_TIER_BASE: Record<
  EstimatorWebsiteTier,
  { min: number; max: number }
> = {
  presence: { min: 1500, max: 2500 },
  sells: { min: 2500, max: 4000 },
  transacts: { min: 2500, max: 4200 },
};

/** Booking, payments, or commerce-adjacent flows. */
export const BOOKING_PAYMENTS_ADD: Record<
  EstimatorBookingPayments,
  { min: number; max: number }
> = {
  /* Booking or payments IS the Transacts tier, so simple booking adds nothing on top of it: it
     is the reason the site is on that rung at all (see `resolveWebsiteTier`). Only genuinely
     harder work adds. A tier that also charged for the thing that defined it would bill twice. */
  none: { min: 0, max: 0 },
  simple: { min: 0, max: 0 },
  complex: { min: 3400, max: 7700 },
};

/**
 * Online shop, added to the website stream. Ownly builds these headless on Shopify: we own the
 * storefront and the design, Shopify keeps payments, checkout, stock and tax.
 *
 * `advanced` covers a large or migrated catalogue, subscriptions and multi-currency. Store setup,
 * loading products and migrating an existing catalogue are chargeable on top of the build.
 */
export const ECOMMERCE_ADD: Record<
  EstimatorEcommerce,
  { min: number; max: number }
> = {
  /* A shop needs a storefront and product setup beyond a simple booking integration. */
  none: { min: 0, max: 0 },
  standard: { min: 2500, max: 3800 },
  advanced: { min: 3000, max: 6600 },
};

/** User accounts, roles, permissions — often pairs with admin. */
/* Customer identity and permissions need more work than embedding a booking provider. */
export const USER_ACCOUNTS_ADD = { min: 2500, max: 3800 } as const;

/** Admin or internal dashboard beyond light CMS usage. */
/* An admin side beyond light CMS use is work on top of any tier, so this one really does add. */
export const ADMIN_DASHBOARD_ADD = { min: 4400, max: 8600 } as const;

/** Custom business system / internal tool (workflow, data, permissions). */
/* The System rung: £10,000. Runs the business, replacing the spreadsheets and the manual admin. */
export const SYSTEM_BASE = { min: 10000, max: 20000 } as const;

/**
 * Native or cross-platform app baseline (one primary native target).
 * Second native platform is discounted vs shipping two fully separate codebases.
 */
/* The App rung: £15,000. A product your customers use on their phone. */
export const APP_SINGLE_NATIVE_BASE = { min: 15000, max: 30000 } as const;

/**
 * A second surface. Half as much again, because the product exists by then and the work is
 * making it live somewhere else rather than building it twice.
 */
export const APP_SECOND_NATIVE_ADD = { min: 7500, max: 15000 } as const;

/** A web surface on top of a phone one. Same step as a second store: one product becomes two. */
export const APP_WEB_SURFACE_ADD = { min: 7500, max: 15000 } as const;

/**
 * Web-only app (no phone). SAME FLOOR AS A ONE-STORE APP, on purpose.
 *
 * One product on one surface is one price. The App rung is described as "a product your customers
 * use on their phone" and that is what most buyers mean, but a product that lives only in a
 * browser is the same build minus the store admin, not a different rung. Surface COUNT is what
 * moves the number. A test asserts it, so do not "fix" this by making web-only cheaper.
 */
export const APP_WEB_ONLY_BASE = { min: 15000, max: 29000 } as const;

/** Automation & third-party integrations (CRM, ERP, payments, data sync). */
export const INTEGRATIONS_ADD: Record<
  EstimatorIntegrations,
  { min: number; max: number }
> = {
  none: { min: 0, max: 0 },
  some: { min: 3200, max: 6800 },
  heavy: { min: 8200, max: 17000 },
};

/** Delivery risk & product ambiguity — multiplies the subtotal before support. */
export const COMPLEXITY_MULTIPLIER: Record<EstimatorComplexity, number> = {
  straightforward: 1,
  moderate: 1.14,
  demanding: 1.32,
};

/**
 * There is deliberately no support/retainer entry in this file.
 *
 * This module prices the **build**, which is a one-off cost. Looking after the thing afterwards is a
 * monthly subscription (£150 / £250 / £500), and those figures live in `RETAINER_TIERS` in
 * `quote-selection.ts`, mirroring the canonical Stripe-backed source in
 * `lead-engine/src/lib/staff-retainer-links.ts`. Do not add a fourth copy of them here.
 *
 * A `SUPPORT_ADD` table used to sit here and got added straight into the project total, so choosing
 * ongoing support inflated a build quote by up to £17,000 for something billed at £250 a month, with
 * no time period shown anywhere in the UI. The quote builder already worked around it by forcing
 * `support: "none"`; the estimator now models the two costs separately instead.
 */

/** Above this upper bound, show an open-ended range (e.g. £80,000+). */
export const OPEN_ENDED_THRESHOLD_GBP = 80000;

/** Round displayed bounds to this step for cleaner labels. */
export const RANGE_ROUND_STEP_GBP = 500;
